/**
 * useMoneyFlowRadar — lädt die Extra-Quotes fürs Geldfluss-Radar und
 * verrechnet sie mit den bereits vorhandenen Regime-Quotes (SPY, GLD, TLT,
 * BTC + 8 Sektoren) zu einem FlowRadar.
 *
 * Ein einziger gebündelter Fetch (Promise.all, fehlertolerant je Symbol),
 * TTL 30 Min — Geldflüsse ändern sich nicht minütlich.
 */

import { useMemo } from 'react';

import { useDataState } from './use-data-state';
import { getMarketDataProvider } from '@/lib/market-data';
import {
  EXTRA_FLOW_SYMBOLS,
  computeMoneyFlowRadar,
  type FlowRadar,
} from '@/lib/money-flow-radar';
import type { PriceQuote } from '@/lib/market-data/types';

interface UseMoneyFlowRadarResult {
  radar: FlowRadar;
  isLoading: boolean;
}

export function useMoneyFlowRadar(
  baseQuotes: Map<string, PriceQuote | null>
): UseMoneyFlowRadarResult {
  const provider = useMemo(() => getMarketDataProvider(), []);

  const extra = useDataState({
    queryKey: ['flow-radar-extra', provider.name],
    queryFn: async (signal) => {
      const out: Record<string, PriceQuote | null> = {};
      await Promise.all(
        EXTRA_FLOW_SYMBOLS.map(async (sym) => {
          try {
            out[sym] = (await provider.getQuote(sym, signal)).data;
          } catch {
            out[sym] = null; // fail-open: ein exotischer ETF darf fehlen
          }
        })
      );
      return out;
    },
    enabled: provider.is_live,
    ttlSeconds: 30 * 60,
    isEmpty: (r) => !r || Object.values(r).every((v) => !v),
  });

  return useMemo(() => {
    const merged = new Map<string, PriceQuote | null>(baseQuotes);
    for (const [sym, q] of Object.entries(extra.state.data ?? {})) {
      merged.set(sym, q);
    }
    return {
      radar: computeMoneyFlowRadar(merged),
      isLoading: extra.state.status === 'loading',
    };
  }, [baseQuotes, extra.state.data, extra.state.status]);
}
